import axios from 'axios';
import { API_BASE_URL } from '../../config/apiConfig';

const headers = { 'Content-Type': 'application/json' };

const handle = (err) => {
    if (err.response?.data) throw err.response.data;
    throw err;
};

// GET CCType for a cost center code
// NOTE: this must be the plain (non-"IT") endpoint — GetCostCenterTypeIT belongs to the
// unrelated IT Asset Transfer module and returns a different shape entirely.
export const getCostCenterType = async (CCType) => {
    try {
        const res = await axios.get(
            `${API_BASE_URL}/Purchase/GetCostCenterType?CCType=${encodeURIComponent(CCType)}`,
            { headers }
        );
        return res.data;
    } catch (err) { handle(err); }
};

// GET whether the current role raises indents through the standard flow or the
// NPCC "Central Store Keeper" consolidation flow (Indent.IndentType: 'NPCC' | other)
export const getIndentFirstLevelDetails = async (Roleid) => {
    try {
        const res = await axios.get(
            `${API_BASE_URL}/Purchase/GetIndentFirstLevelDetails?Roleid=${encodeURIComponent(Roleid)}`,
            { headers }
        );
        return res.data;
    } catch (err) { handle(err); }
};

// GET list of capital master types allowed for a CC type
export const getCapitalMasterTypes = async (Type) => {
    try {
        const res = await axios.get(
            `${API_BASE_URL}/Purchase/GetCapitalMasterTypes?Type=${encodeURIComponent(Type)}`,
            { headers }
        );
        return res.data;
    } catch (err) { handle(err); }
};

// GET master item codes for a Capital Material Type — when this returns rows, the
// legacy page shows a second cascading dropdown before category selection.
export const getMaterialItemCodes = async (Materialtype) => {
    try {
        const res = await axios.get(
            `${API_BASE_URL}/Purchase/MaterialItemcode?Materialtype=${encodeURIComponent(Materialtype)}`,
            { headers }
        );
        return res.data;
    } catch (err) { handle(err); }
};

// GET item categories scoped to a Capital/Performing cost center's material type
// (distinct source from GetIndentcategoriesDetails, used for non-capital CC types)
export const getCategoriesCapital = async (CType, Materialtype) => {
    try {
        const res = await axios.get(
            `${API_BASE_URL}/Purchase/GetCatogoriesCapital?CType=${encodeURIComponent(CType)}&Materialtype=${encodeURIComponent(Materialtype)}`,
            { headers }
        );
        return res.data;
    } catch (err) { handle(err); }
};

// GET staged indent line items for the current CC + capital selection.
// CapitalItemcode/Materialtype are omitted from the query string entirely when they
// don't apply (never sent as ''): spGetItemCodesdetailsbyCC branches on IS NULL, and
// an empty string would route into the wrong SQL branch and silently return nothing.
// GetItemCodesbyCC's backend signature carries default values for these two params
// specifically so omitting them doesn't 404 ("No action found") — see PurchaseController.cs.
export const getItemCodesByCC = async (CCode, CapitalItemcode, Materialtype) => {
    try {
        const params = new URLSearchParams({ CCode });
        if (CapitalItemcode) params.append('CapitalItemcode', CapitalItemcode);
        if (Materialtype)    params.append('Materialtype', Materialtype);
        const res = await axios.get(
            `${API_BASE_URL}/Purchase/GetItemCodesbyCC?${params.toString()}`,
            { headers }
        );
        return res.data;
    } catch (err) { handle(err); }
};

// GET item categories for a cost center (CType = CCCode)
export const getIndentCategories = async (CType) => {
    try {
        const res = await axios.get(
            `${API_BASE_URL}/Purchase/GetIndentcategoriesDetails?CType=${encodeURIComponent(CType)}`,
            { headers }
        );
        return res.data;
    } catch (err) { handle(err); }
};

// GET item search autocomplete (Typ: '1' = by code, '2' = by name)
export const getAutoComplete = async (Pfx, Typ, Cat) => {
    try {
        const res = await axios.get(
            `${API_BASE_URL}/Purchase/GetAutoComplete?Pfx=${encodeURIComponent(Pfx)}&Typ=${encodeURIComponent(Typ)}&Cat=${encodeURIComponent(Cat)}`,
            { headers }
        );
        return res.data;
    } catch (err) { handle(err); }
};

// POST — add one item to staging table
// payload: { ItemCode, Costcenter, CapitalMaterialType, CapitalMaterials }
export const saveIndentItems = async (payload) => {
    try {
        const res = await axios.post(
            `${API_BASE_URL}/Purchase/SaveIndentItems`,
            payload,
            { headers, timeout: 30000 }
        );
        return res.data;
    } catch (err) { handle(err); }
};

// POST — remove a staged line item
// payload: { IndentListId }
export const deleteIndentItem = async (payload) => {
    try {
        const res = await axios.post(
            `${API_BASE_URL}/Purchase/DeleteIndentItem`,
            payload,
            { headers, timeout: 30000 }
        );
        return res.data;
    } catch (err) { handle(err); }
};

// GET stock summary popup for a specific item at a CC
export const getIndentItemSummaryPopup = async (Itemcode, CCcode) => {
    try {
        const res = await axios.get(
            `${API_BASE_URL}/Purchase/IndentItemcodeSummaryPopup?Itemcode=${encodeURIComponent(Itemcode)}&CCcode=${encodeURIComponent(CCcode)}`,
            { headers }
        );
        return res.data;
    } catch (err) { handle(err); }
};

// POST — finalize and submit the indent
// payload: { IndentListIds, Quantitys, Amounts, TotalAmount, Date, Remarks, Costcenter, Createdby, RoleID }
export const saveIndent = async (payload) => {
    try {
        const res = await axios.post(
            `${API_BASE_URL}/Purchase/SaveIndent`,
            payload,
            { headers, timeout: 30000 }
        );
        return res.data;
    } catch (err) { handle(err); }
};
